// A small 2D preview of a coordination area: the polygon as it sits on
// its sheet, with the rest of the drawing dimmed so the zone reads at a
// glance. Used inside tooltips, where a name alone ("HYDRONIC WATER
// PIPING TO GL 45-M") tells you nothing about WHERE on the floor it is.
import React from "react";

export default function AreaShapePreview({
  area, width = 240, height = 170, showDates = true,
  // Zoom the sheet so THIS area fills the frame. A zone is a few percent
  // of a full floor plan, so showing the whole sheet renders it as a
  // speck - which tells you it exists but not what it covers.
  zoom = false, pad = 12,
}) {
  if (!area) return null;
  const pts = (area.points || []).map((p) => `${p.x},${p.y}`).join(" ");
  const color = area.color || "#3b82f6";

  // Bounding box of the polygon, in the same 0-100 space the points are
  // stored in, padded so the zone is not flush against the edge.
  let view = null;
  if (zoom && (area.points || []).length >= 3) {
    const xs = area.points.map((p) => p.x);
    const ys = area.points.map((p) => p.y);
    const minX = Math.max(0, Math.min(...xs) - pad);
    const minY = Math.max(0, Math.min(...ys) - pad);
    const maxX = Math.min(100, Math.max(...xs) + pad);
    const maxY = Math.min(100, Math.max(...ys) + pad);
    const w = Math.max(1, maxX - minX);
    const h = Math.max(1, maxY - minY);
    // Fit the longer side so nothing is cropped out of the zone.
    const scale = 100 / Math.max(w, h);
    view = { minX, minY, w, h, scale };
  }
  return (
    <div style={{ width }}>
      <div
        style={{
          position: "relative",
          width,
          height,
          borderRadius: 4,
          overflow: "hidden",
          background: "#0f172a",
          border: "1px solid rgba(255,255,255,.15)",
        }}
      >
        {area.sheetImageUrl ? (
          <img
            src={area.sheetImageUrl}
            alt=""
            style={view ? {
              position: "absolute",
              width: `${view.scale * 100}%`,
              height: `${view.scale * 100}%`,
              left: `${-view.minX * view.scale}%`,
              top: `${-view.minY * view.scale}%`,
              objectFit: "fill", opacity: 0.75,
            } : {
              position: "absolute", inset: 0, width: "100%", height: "100%",
              objectFit: "fill", opacity: 0.55, filter: "grayscale(0.4)",
            }}
          />
        ) : null}
        <svg
          viewBox={view ? `${view.minX} ${view.minY} ${view.w} ${view.h}` : "0 0 100 100"}
          preserveAspectRatio={view ? "xMidYMid meet" : "none"}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
        >
          {pts && (
            <polygon
              points={pts}
              fill={color}
              fillOpacity="0.55"
              stroke={color}
              strokeWidth={view ? "0.4" : "0.8"}
              vectorEffect="non-scaling-stroke"
            />
          )}
        </svg>
      </div>
      <div style={{ marginTop: 6, fontSize: 12, lineHeight: 1.45 }}>
        <div style={{ fontWeight: 600 }}>{area.name}</div>
        {(area.level || area.code) && (
          <div style={{ opacity: 0.75 }}>
            {[area.level, area.code].filter(Boolean).join(" · ")}
          </div>
        )}
        {showDates && (area.dates || []).length > 0 && (
          <div style={{ marginTop: 4 }}>
            {(area.dates || []).map((d, i) => (
              <div key={i} style={{ opacity: 0.9 }}>
                {d.date ? new Date(d.date).toLocaleDateString() : "date not readable"}
                {" — "}
                {d.label}
              </div>
            ))}
          </div>
        )}
        {typeof area.linkedCount === "number" && (
          <div style={{ marginTop: 4, opacity: 0.75 }}>
            {area.linkedCount} linked item{area.linkedCount === 1 ? "" : "s"}
          </div>
        )}
      </div>
    </div>
  );
}
